import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { LogOut, ExternalLink } from "lucide-react";
import ingroooveLogo from "../assets/logos/ingroove-logo.png";
import omnyxLogoText from "../assets/logos/omnyx-logo-text.png";

import { useEffect, useState } from "react";
import ingrooveApi from "../services/ingroove/api";

const products = [
  {
    id: "ingroove",
    name: "Ingroove",
    description: "Event platform — users, hubs, tickets, payouts",
    path: "/ingroove/overview",
    logo: ingroooveLogo,
    gradient: "from-teal-500/20 to-blue-500/20",
    border: "border-teal-500/20",
    available: true,
  },
  {
    id: "project-two",
    name: "Mgozi",
    description: "Coming soon",
    path: null,
    logo: null,
    gradient: "from-border to-border",
    border: "border-border",
    available: false,
  },
  {
    id: "project-three",
    name: "Transi",
    description: "Coming soon",
    path: null,
    logo: null,
    gradient: "from-border to-border",
    border: "border-border",
    available: false,
  },
];

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
};

const getInitials = (user) => {
  if (!user) return "AO";
  return `${user.name?.[0] ?? ""}${user.surname?.[0] ?? ""}`.toUpperCase();
};

const AppLauncher = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const [pendingPayouts, setPendingPayouts] = useState(null);

  useEffect(() => {
    const fetchPendingPayouts = async () => {
      try {
        const res = await ingrooveApi.get("/payout/pending/count");
        if (res.data.isSuccess) setPendingPayouts(res.data.data);
      } catch {
        // non-critical, fail silently
      }
    };
    fetchPendingPayouts();
  }, []);

  return (
    <div className="min-h-screen bg-bg-base flex flex-col">
      {/* Top bar */}
      <div className="h-14 bg-bg-topbar border-b border-border flex items-center justify-between px-6">
        <div className="flex items-center gap-2">
          <img
            src={omnyxLogoText}
            alt="Omnyx"
            className="h-7 object-contain rounded-md"
          />
          <span className="text-xs bg-bg-surface border border-border rounded-full px-2.5 py-0.5 text-text-muted ml-1">
            Internal Portal
          </span>
        </div>

        <div className="flex items-center gap-3">
          {/* Avatar + name */}
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full omnyx-gradient-bg flex items-center justify-center text-white text-xs font-bold">
              {getInitials(user)}
            </div>
            <span className="text-xs text-text-secondary hidden sm:block">
              {user?.email}
            </span>
          </div>

          <button
            onClick={handleLogout}
            className="flex items-center gap-1.5 text-xs text-text-muted hover:text-red-400 transition px-2 py-1 rounded-lg hover:bg-red-500/10"
          >
            <LogOut size={13} />
            Sign out
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 max-w-4xl mx-auto w-full px-6 py-12">
        {/* Greeting */}
        <div className="mb-12">
          <h1 className="text-2xl font-semibold text-text-primary mb-1">
            {getGreeting()},{" "}
            <span className="omnyx-gradient-text">
              {user?.name ?? "Admin"} 👋
            </span>
          </h1>
          <p className="text-text-muted text-sm">
            Manage all Omnyx products from one place.
          </p>
        </div>

        {/* Products */}
        <div className="mb-4">
          <p className="text-xs text-text-muted uppercase tracking-widest mb-5">
            Products
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {products.map((product) => (
              <div
                key={product.id}
                onClick={() => product.available && navigate(product.path)}
                className={`relative bg-bg-surface border rounded-2xl p-5 transition-all duration-200 ${
                  product.available
                    ? "cursor-pointer hover:border-primary hover:bg-bg-elevated glow-teal group"
                    : "opacity-40 cursor-default border-border"
                } ${product.border}`}
              >
                {/* Icon / Logo */}
                <div
                  className={`w-11 h-11 rounded-xl bg-gradient-to-br ${product.gradient} border ${product.border} flex items-center justify-center mb-4 overflow-hidden`}
                >
                  {product.logo ? (
                    <img
                      src={product.logo}
                      alt={product.name}
                      className="w-7 h-7 object-contain"
                    />
                  ) : (
                    <span className="text-xl">
                      {product.id === "ingroove" ? "🎵" : "📦"}
                    </span>
                  )}
                </div>
                {product.id === "ingroove" && pendingPayouts > 0 && (
                  <div className="absolute top-4 right-4 flex items-center gap-1 bg-amber-500/10 border border-amber-500/20 rounded-full px-2 py-0.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                    <span className="text-xs text-amber-400 font-medium">
                      {pendingPayouts} payout{pendingPayouts !== 1 ? "s" : ""}
                    </span>
                  </div>
                )}
                <p className="font-semibold text-sm text-text-primary mb-1">
                  {product.name}
                </p>
                <p className="text-xs text-text-muted leading-relaxed">
                  {product.description}
                </p>

                {/* Arrow on hover */}
                {product.available && (
                  <ExternalLink
                    size={14}
                    className="absolute top-4 right-4 text-primary opacity-0 group-hover:opacity-100 transition"
                  />
                )}

                {/* Coming soon tag */}
                {!product.available && (
                  <span className="absolute top-4 right-4 text-xs bg-bg-base border border-border text-text-hint px-2 py-0.5 rounded-full">
                    Soon
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-border py-4 px-6 text-center">
        <p className="text-xs text-text-hint">
          Omnyx Internal Portal — Not for public use
        </p>
      </div>
    </div>
  );
};

export default AppLauncher;
